import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';

export default function SchemeCodes() {
  const api  = useApi();
  const { user } = useAuth();

  const [schemes,       setSchemes]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [modal,         setModal]         = useState(null); // null | 'add' | row
  const [form,          setForm]          = useState({ code: '', plan_name: '', medical_aid: '', destination: '' });
  const [saving,        setSaving]        = useState(false);
  const [deleting,      setDeleting]      = useState(null);

  // Filters
  const [fDest,    setFDest]    = useState('');
  const [fMa,      setFMa]      = useState('');
  const [fPlan,    setFPlan]    = useState('');
  const [fCode,    setFCode]    = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { setSchemes(await api.get('/reports/schemes')); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // Unique filter option lists derived from data
  const destinations = [...new Set(schemes.map(s => s.destination).filter(Boolean))].sort();
  const medicalAids  = [...new Set(schemes.map(s => s.medical_aid).filter(Boolean))].sort();
  const plans        = [...new Set(schemes.map(s => s.plan_name).filter(Boolean))].sort();

  // Apply all four filters
  const filtered = schemes.filter(s => {
    if (fDest && s.destination !== fDest)                              return false;
    if (fMa   && s.medical_aid !== fMa)                                return false;
    if (fPlan && s.plan_name   !== fPlan)                              return false;
    if (fCode && !s.code.toLowerCase().includes(fCode.toLowerCase())) return false;
    return true;
  });

  const clearFilters = () => { setFDest(''); setFMa(''); setFPlan(''); setFCode(''); };

  const openAdd = () => {
    setForm({ code: '', plan_name: '', medical_aid: '', destination: '' });
    setModal('add');
  };

  const openEdit = (row) => {
    setForm({ code: row.code, plan_name: row.plan_name, medical_aid: row.medical_aid, destination: row.destination || '' });
    setModal(row);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (modal === 'add') {
        await api.post('/reports/schemes', form);
      } else {
        await api.put(`/reports/schemes/${modal.id}`, form);
      }
      setModal(null);
      load();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`Delete scheme code '${code}'?`)) return;
    setDeleting(id);
    try { await api.del(`/reports/schemes/${id}`); load(); }
    catch (e) { alert(e.message); }
    finally { setDeleting(null); }
  };

  const canDelete = (user?.accesslevel ?? 0) >= 3;

  return (
    <div className="animate-in">
      {/* Page header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Scheme Codes</h1>
          <div className="page-subtitle">Destination scheme / option lookup table</div>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Scheme Code</button>
      </div>

      {/* Filter grid */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Destination</label>
            <select className="form-select" value={fDest} onChange={e => setFDest(e.target.value)}>
              <option value="">All</option>
              {destinations.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Medical Aid</label>
            <select className="form-select" value={fMa} onChange={e => setFMa(e.target.value)}>
              <option value="">All</option>
              {medicalAids.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Plan / Option</label>
            <select className="form-select" value={fPlan} onChange={e => setFPlan(e.target.value)}>
              <option value="">All</option>
              {plans.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Code (going_to_sub)</label>
            <input className="form-input" placeholder="Search code…"
              value={fCode} onChange={e => setFCode(e.target.value)}
              style={{ fontFamily: 'var(--font-mono)' }} />
          </div>
        </div>
        {(fDest || fMa || fPlan || fCode) && (
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {filtered.length} of {schemes.length} records
            </span>
            <button className="btn btn-ghost btn-sm" onClick={clearFilters}>✕ Clear filters</button>
          </div>
        )}
      </div>

      {/* Data grid */}
      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
            {schemes.length === 0
              ? 'No scheme codes found. Click + Add Scheme Code to get started.'
              : 'No records match the current filters.'}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Destination</th>
                <th>Medical Aid</th>
                <th>Plan / Option</th>
                <th>Updated</th>
                <th style={{ width: 120 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => (
                <tr key={s.id}>
                  <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--accent)' }}>
                    {s.code}
                  </td>
                  <td style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                    {s.destination || '—'}
                  </td>
                  <td style={{ fontWeight: 500 }}>{s.medical_aid || '—'}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{s.plan_name || '—'}</td>
                  <td style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                    {s.updated_at ? new Date(s.updated_at).toLocaleDateString('en-ZA') : '—'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => openEdit(s)}>Edit</button>
                      {canDelete && (
                        <button className="btn btn-danger btn-sm"
                          disabled={deleting === s.id}
                          onClick={() => handleDelete(s.id, s.code)}>
                          {deleting === s.id ? '…' : 'Delete'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add / Edit modal */}
      {modal && createPortal(
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          background: 'rgba(0,0,0,0.5)',
          padding: '48px 16px', overflowY: 'auto',
        }}>
          <div className="card" style={{ width: '100%', maxWidth: 480, padding: 28 }}>
            <h3 style={{ margin: '0 0 20px', fontSize: 15 }}>
              {modal === 'add' ? 'Add Scheme Code' : `Edit — ${modal.code}`}
            </h3>

            {/* Form grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Code (going_to_sub)</label>
                <input className="form-input"
                  value={form.code}
                  onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                  disabled={modal !== 'add'}
                  placeholder="e.g. BCLA"
                  style={{ fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}
                />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Destination</label>
                <input className="form-input"
                  value={form.destination}
                  onChange={e => setForm(f => ({ ...f, destination: e.target.value }))}
                  placeholder="e.g. ipbm"
                  list="dest-list"
                />
                <datalist id="dest-list">
                  {destinations.map(d => <option key={d} value={d} />)}
                </datalist>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Medical Aid</label>
                <input className="form-input"
                  value={form.medical_aid}
                  onChange={e => setForm(f => ({ ...f, medical_aid: e.target.value }))}
                  placeholder="e.g. Bonitas"
                  list="ma-list"
                />
                <datalist id="ma-list">
                  {medicalAids.map(m => <option key={m} value={m} />)}
                </datalist>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Plan / Option</label>
                <input className="form-input"
                  value={form.plan_name}
                  onChange={e => setForm(f => ({ ...f, plan_name: e.target.value }))}
                  placeholder="e.g. BonClassic"
                  list="plan-list"
                />
                <datalist id="plan-list">
                  {plans.map(p => <option key={p} value={p} />)}
                </datalist>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24 }}>
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave}
                disabled={saving || !form.code || !form.plan_name || !form.medical_aid}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
